import { Link } from "@tanstack/react-router";
import { Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntityComboboxMulti } from "@/components/ui/entity-combobox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useManagementCompetencies,
  useSetManagementCompetencies,
} from "@/hooks/use-management-competencies";

/**
 * The management capability set — WI-311.
 *
 * What a direct report is asked about when they review their manager. One
 * company-wide set rather than one per role: managing people is the same job
 * whatever discipline you manage in (Kori Deitz, 2026-10-05).
 *
 * Empty until someone fills it in, and the screen says what that means rather
 * than showing a bare "nothing here" — an empty set is the difference between
 * a direct-report review that asks questions and one that asks none.
 */
export function SettingsManagementCapabilitiesPage() {
  const { data: rows = [], isLoading, isError, error, refetch } = useManagementCompetencies();
  const [editing, setEditing] = useState(false);

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load the management capability set"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        The competencies a direct report is asked about when they review their manager. These
        replace the manager's own role requirements on that one form — a peer reviewing the same
        manager still answers on their role.
      </p>

      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
          <Pencil className="mr-1 h-3.5 w-3.5" />
          Edit the set
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-10 w-full" />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No management capabilities chosen yet"
          description="Until something is here, a review by a direct report asks no competency questions."
        />
      ) : (
        <ul className="flex flex-wrap gap-2">
          {rows.map((row) => (
            <li
              key={row.id}
              className="inline-flex items-center gap-2 rounded-md border bg-card px-3 py-1.5 text-sm"
            >
              <Link
                to="/dashboard/competencies/$competencyId"
                params={{ competencyId: row.competencyId }}
                className="text-primary hover:underline"
              >
                {row.competencyName}
              </Link>
            </li>
          ))}
        </ul>
      )}

      <EditDialog
        open={editing}
        onOpenChange={setEditing}
        current={rows.map((r) => r.competencyId)}
      />
    </div>
  );
}

interface EditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  current: string[];
}

function EditDialog({ open, onOpenChange, current }: EditDialogProps) {
  const [draft, setDraft] = useState<string[]>(current);
  const save = useSetManagementCompetencies();

  // Re-seed from the server every time the dialog opens, so a cancelled edit
  // is genuinely discarded rather than kept around for the next open.
  useEffect(() => {
    if (open) setDraft(current);
  }, [open, current]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Management capabilities</DialogTitle>
          <DialogDescription>
            Choose the competencies a direct report rates their manager on.
          </DialogDescription>
        </DialogHeader>

        <EntityComboboxMulti
          entity="competencies"
          value={draft}
          onChange={setDraft}
          filters={{ status: "active" }}
          placeholder="Search and select competencies…"
          searchPlaceholder="Search competencies…"
        />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={save.isPending}
            onClick={() =>
              save.mutate(
                draft.map((competencyId) => ({ competencyId })),
                { onSuccess: () => onOpenChange(false) },
              )
            }
          >
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
